import { Box, Typography } from '@mui/material';

import WhyImportantLink from '../../components/WhyImportantLink';
import { ACCOUNTING_SETUP_HELP_CONTEXT } from '../../hooks/useOpenAccountingSetupHelp';

const TRANSFER_HELP_QUESTION =
    'How do I transfer reimbursement amounts from my business account to my personal account?';

interface ReimbursementSummaryProps {
    totalAmount: number;
    onTransferHelpClick?: () => void;
}

export default function ReimbursementSummary({
    totalAmount,
    onTransferHelpClick = undefined,
}: Readonly<ReimbursementSummaryProps>) {
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
        }).format(amount);
    };

    return (
        <Box
            sx={{
                backgroundColor: (theme) => theme.palette.cement[50],
                borderRadius: '12px',
                border: 1,
                borderColor: 'cement.100',
                p: 3,
                mt: 3,
            }}>
            {/* Header with title and amount */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                }}>
                <Typography
                    variant="h6"
                    sx={{
                        fontFamily: 'Mier B',
                        fontWeight: 600,
                        fontSize: '16px',
                        color: (theme) => theme.palette.text.primary,
                    }}>
                    Total reimburseable amount
                </Typography>
                <Typography
                    variant="h4"
                    sx={{
                        fontFamily: 'Mier B',
                        fontWeight: 600,
                        fontSize: '24px',
                        color: (theme) => theme.palette.text.primary,
                    }}>
                    {formatCurrency(totalAmount)}
                </Typography>
            </Box>

            {/* Explanatory text */}
            <Typography
                variant="body1"
                sx={{
                    fontFamily: 'Mier B',
                    fontWeight: 400,
                    fontSize: '14px',
                    color: (theme) => theme.palette.text.primary,
                    lineHeight: 1.5,
                    mb: 2,
                }}>
                Transfer this amount from your business account to your personal account. This creates the required
                record and keeps your reimbursement tax-free.
            </Typography>

            {/* Help link */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WhyImportantLink
                    context={ACCOUNTING_SETUP_HELP_CONTEXT.reimbursement}
                    title="How do I transfer the amount?"
                    helpQuestion={TRANSFER_HELP_QUESTION}
                    onClick={onTransferHelpClick}
                />
            </Box>
        </Box>
    );
}
